/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useBulkEmailGeneration,
  useBulkEmailTest,
} from "@/hooks/use-emailHealth";
import { useGetUniqueDomains } from "@/hooks/use-mailbox";
import { SUBSCRIPTION_QUERY_KEY } from "@/hooks/use-subscription";
import { downloadClientSideCSV } from "@/lib/utils";
import { useServices } from "@/services/hooks/useServices";
import { AuthProvider, espTypeDescription } from "@/types/common";
import { useQueryClient } from "@tanstack/react-query";
import { Download, Info, Loader2, Mail, Search, X } from "lucide-react";
import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import CopyButton from "../copy-button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Alert, AlertDescription } from "../ui/alert";
import { SearchInput } from "../ui/search-input";

interface BulkTestPopupProps {
  isOpen: boolean;
  onClose: () => void;
  domains: string[];
}

export default function BulkTestPopup({
  isOpen,
  onClose,
  domains = [],
}: BulkTestPopupProps) {
  const [selectedEspType, setSelectedEspType] = useState<AuthProvider>(AuthProvider.SMTP);
  const { data: uniqueDomains, isLoading: isLoadingUniqueDomains } =
    useGetUniqueDomains(selectedEspType);
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [testCount, setTestCount] = useState<number>(1);
  // const [testEmails, setTestEmails] = useState<{ domain: string, testEmail: string }[]>([])
  const [activeTab, setActiveTab] = useState<string>("automatic");
  const [loading, setLoading] = useState<boolean>(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState<boolean>(false);

  const [numberOfManualTests, setNumberOfManualTests] = useState<number>(1);
  const {
    mutateAsync: generateBulkAliases,
    isPending: isGeneratingBulkAliases,
    data: testEmails,
    reset: resetTestEmails,
  } = useBulkEmailGeneration();

  const { runBulkEmailTest, isRunningBulkTest } = useBulkEmailTest();
  const { mailboxService } = useServices();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState("");

  // Check if domain has connected mailbox

  const handleSelectDomain = (domain: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedDomains([...selectedDomains, domain]);
    } else {
      setSelectedDomains(selectedDomains.filter((d) => d !== domain));
    }
  };

  const handleSelectAllDomains = (checked: boolean) => {
    if (checked) {
      setSelectedDomains(uniqueDomains || []);
    } else {
      setSelectedDomains([]);
    }
  };

  const handleRunAutomaticTests = async () => {
    try {
      setLoading(true);
      setShowLoadingScreen(true); // Show loading screen before API call
      await mailboxService.runBulkEmailTest(selectedDomains, selectedEspType);
      queryClient.invalidateQueries({
        queryKey: [SUBSCRIPTION_QUERY_KEY],
      });
      toast.success(`Bulk email test started for ${selectedDomains.length} domains using ${selectedEspType.replace('_', ' ')} configuration`);
      window.cioanalytics?.track("email_health_test_run_bulk_connected", {
        count: selectedDomains.length,
        espType: selectedEspType,
      });
      onClose();
    } catch (error) {
      console.log(error);
      toast.error("Failed to run bulk email test");
    } finally {
      setLoading(false);
      setShowLoadingScreen(false);
    }
  };

  const handleRunManualTests = () => {
    // For manual tests, show loading screen with expected time
    setShowLoadingScreen(true);

    window.cioanalytics?.track("email_health_test_run_bulk_unconnected", {
      count: numberOfManualTests,
      espType: selectedEspType,
    });
    // Simulate loading for a brief moment to show the message
    setTimeout(() => {
      setShowLoadingScreen(false);
      toast.success(
        "Test emails generated. Please send emails to complete the test."
      );
      onClose();
    }, 2000);
  };

  useEffect(() => {
    setSelectedDomains(domains);
  }, [domains]);

  // Add filtered domains logic
  const filteredDomains = useMemo(() => {
    if (!searchTerm) return uniqueDomains;

    return uniqueDomains?.filter((domain) =>
      domain.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [uniqueDomains, searchTerm]);

  // Reset ESP type when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedEspType(AuthProvider.SMTP);
      setSearchTerm("");
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        {showLoadingScreen ? (
          <div className="py-8 flex flex-col items-center justify-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <h3 className="text-lg font-medium text-center">
              Processing your request
            </h3>
            <p className="text-sm text-center text-muted-foreground">
              {activeTab === "automatic"
                ? `This will take approximately ${
                    selectedDomains.length * 2
                  } minutes (2 minutes per domain).`
                : "This will take approximately 5 minutes to generate results."}
            </p>
            <p className="text-sm text-center text-muted-foreground mt-2">
              You'll able to see the results in the email health tab.
            </p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Bulk Email Test</DialogTitle>
              <DialogDescription>
                Test email deliverability across multiple domains
              </DialogDescription>
            </DialogHeader>

            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="automatic">Automatic Testing</TabsTrigger>
                <TabsTrigger value="manual">Manual Testing</TabsTrigger>
              </TabsList>

              <TabsContent value="automatic" className="space-y-4 mt-4">
                <Alert variant="info" className="p-4 rounded-md">
                  <Info className="size-4 mt-0.5 flex-shrink-0" />
                  <AlertDescription>
                    Select domains with connected mailboxes to run automatic
                    deliverability tests. Our system will automatically send and
                    verify test emails.
                  </AlertDescription>
                </Alert>

                {/* ESP Type Selection */}
                <div className="space-y-2">
                  <Label htmlFor="esp-type" className="text-sm font-medium">
                    Email Service Provider Type
                  </Label>
                  <Select value={selectedEspType} onValueChange={(value) => setSelectedEspType(value as AuthProvider)}>
                    <SelectTrigger id="esp-type">
                      <SelectValue placeholder="Select ESP type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={AuthProvider.SMTP}>SMTP - Standard SMTP Server</SelectItem>
                      <SelectItem value={AuthProvider.GOOGLE}>Google - Gmail/Google Workspace</SelectItem>
                      <SelectItem value={AuthProvider.MICROSOFT}>Microsoft - Outlook/Office 365</SelectItem>
                      <SelectItem value={AuthProvider.AWS_API}>Amazon SES</SelectItem>
                      <SelectItem value={AuthProvider.SENDGRID_API}>SendGrid  </SelectItem>
                      <SelectItem value={AuthProvider.GOOGLE_WORKSPACE_SMTP}>Google Workspace SMTP - Google Workspace SMTP</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Select the email service provider type that matches your domains' configuration
                  </p>
                </div>

                {selectedDomains.length > 0 && (
                  <div className="max-h-[60px] overflow-y-auto flex flex-wrap gap-1 ">
                    {selectedDomains?.map((domain: any) => (
                      <Badge
                        key={domain}
                        variant="outline"
                        className="cursor-pointer rounded-sm px-1.5"
                      >
                        <span className="font-normal">{domain}</span>
                        <X
                          className="size-3 ml-1 text-muted-foreground hover:text-foreground"
                          onClick={() =>
                            setSelectedDomains((prev) =>
                              prev.filter((d) => d !== domain)
                            )
                          }
                        />
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Add Search Input */}
                <div className="relative w-full">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search by domains..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 bg-background"
                  />
                  {searchTerm && (
                    <button
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                      onClick={() => setSearchTerm("")}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="border rounded-md overflow-hidden">
                  <div className="flex items-center p-3 border-b bg-gray-50 dark:bg-secondary">
                    <Checkbox
                      id="select-all"
                      checked={
                        filteredDomains &&
                        filteredDomains.length > 0 &&
                        selectedDomains.length === filteredDomains.length
                      }
                      disabled={
                        isLoadingUniqueDomains || filteredDomains?.length === 0
                      }
                      onCheckedChange={(checked) =>
                        handleSelectAllDomains(checked === true)
                      }
                    />
                    <div className="flex items-center gap-2 justify-between w-full">
                      <Label
                        htmlFor="select-all"
                        className="ml-3 flex-1 cursor-pointer font-medium text-xs"
                      >
                        Select All Domains
                      </Label>
                      <span className="text-xs text-muted-foreground">
                        Total: {filteredDomains?.length} domains
                      </span>
                    </div>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto w-full">
                    {isLoadingUniqueDomains ? (
                      <div className="flex items-center justify-center gap-2 p-3 py-6">
                        <Loader2 className="size-4 animate-spin" />
                        <p className="text-sm text-muted-foreground">
                          Fetching domains...
                        </p>
                      </div>
                    ) : (
                      filteredDomains?.map((domain: any) => (
                        <div
                          key={domain}
                          className="flex items-center p-3 border-b last:border-b-0 w-full min-w-0"
                        >
                          <Checkbox
                            id={`domain-${domain}`}
                            checked={selectedDomains.includes(domain)}
                            onCheckedChange={(checked) =>
                              handleSelectDomain(domain, checked === true)
                            }
                            className="flex-shrink-0"
                          />
                          <Label
                            htmlFor={`domain-${domain}`}
                            className="ml-3 flex-1 cursor-pointer font-normal truncate min-w-0"
                          >
                            {domain}
                          </Label>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {selectedDomains.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No domains with connected mailboxes selected. Please select
                    at least one domain or switch to Manual Testing.
                  </p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      {selectedDomains.length} domains selected
                    </p>
                    <div className="text-xs text-blue-600">
                    ESP Type: <span className="font-medium">{espTypeDescription[selectedEspType as keyof typeof espTypeDescription]}</span>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="manual" className="space-y-4 mt-4">
                <Alert variant="info">
                  <Info className="size-4 mt-0.5 flex-shrink-0" />
                  <div className="space-y-2">
                    <p className="text-sm">
                      Generate the required test email addresses and manually
                      send emails to them to complete the test.
                    </p>
                    <p className="text-sm">
                      After sending, allow 2-5 minutes per domain for the email
                      health report to generate. After test completion, you'll
                      be able to see the results in the Email Health grid.
                    </p>
                  </div>
                </Alert>

                {/* ESP Type Selection for Manual Tests */}
                <div className="space-y-2">
                  <Label htmlFor="esp-type-manual" className="text-sm font-medium">
                    Email Service Provider Type
                  </Label>
                  <Select value={selectedEspType} onValueChange={(value) => setSelectedEspType(value as AuthProvider)}>
                    <SelectTrigger id="esp-type-manual">
                      <SelectValue placeholder="Select ESP type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={AuthProvider.SMTP}>SMTP - Standard SMTP Server</SelectItem>
                      <SelectItem value={AuthProvider.GOOGLE}>Google - Gmail/Google Workspace</SelectItem>
                      <SelectItem value={AuthProvider.MICROSOFT}>Microsoft - Outlook/Office 365</SelectItem>
                      <SelectItem value={AuthProvider.AWS_API}>AWS API - Amazon SES API</SelectItem>
                      <SelectItem value={AuthProvider.AWS_SMTP}>AWS SMTP - Amazon SES SMTP</SelectItem>
                      <SelectItem value={AuthProvider.SENDGRID_SMTP}>SendGrid SMTP - SendGrid SMTP Relay</SelectItem>
                      <SelectItem value={AuthProvider.SENDGRID_API}>SendGrid API - SendGrid Web API</SelectItem>
                      <SelectItem value={AuthProvider.GOOGLE_WORKSPACE_SMTP}>Google Workspace SMTP - Google Workspace SMTP</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Select the email service provider type for your manual tests
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <Label>Number of Manual Tests</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={numberOfManualTests}
                      disabled={testEmails && testEmails.length > 0}
                      onChange={(e) =>
                        setNumberOfManualTests(Number(e.target.value))
                      }
                    />

                    {!testEmails || testEmails.length === 0 ? (
                      <Button
                        variant="outline"
                        onClick={() => generateBulkAliases(numberOfManualTests)}
                        className="w-full"
                        disabled={isGeneratingBulkAliases}
                        isLoading={isGeneratingBulkAliases}
                      >
                        {isGeneratingBulkAliases
                          ? "Generating..."
                          : "Generate Test Emails"}
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          setNumberOfManualTests(1);
                          resetTestEmails();
                        }}
                      >
                        Reset
                      </Button>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  {testEmails && testEmails.length > 0 && (
                    <div className="border rounded-md p-3 bg-gray-50 w-full">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium">Send emails to:</p>

                        <DownloadEmailsButton testEmails={testEmails} />
                      </div>
                      <div className="space-y-2 max-h-[200px] overflow-y-auto w-full">
                        {testEmails.map((email) => (
                          <div className="flex items-center gap-2 text-sm w-full min-w-0">
                            <Mail size={16} className="text-gray-500 flex-shrink-0" />
                            <span className="flex-1 truncate min-w-0">{email}</span>
                            <CopyButton
                              text={email}
                              size="sm"
                              variant="ghost"
                              className="text-xs flex-shrink-0"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={onClose}
                // className="sm:mr-auto"
              >
                Cancel
              </Button>
              {activeTab === "automatic" ? (
                <Button
                  onClick={handleRunAutomaticTests}
                  disabled={
                    selectedDomains.length === 0 || isRunningBulkTest || loading
                  }
                  isLoading={isRunningBulkTest || loading}
                >
                  {isRunningBulkTest || loading
                    ? "Running Tests..."
                    : `Run ${selectedEspType.replace('_', ' ')} Tests`}
                </Button>
              ) : (
                <Button
                  onClick={handleRunManualTests}
                  disabled={!testEmails || loading}
                  isLoading={loading}
                >
                  Continue
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const DownloadEmailsButton = ({ testEmails }: { testEmails: string[] }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownloadEmails = () => {
    try {
      if (testEmails) {
        setIsDownloading(true);
        downloadClientSideCSV(testEmails);
      } else {
        toast.error("No emails to download");
      }
    } catch (error) {
      console.error("Error downloading CSV:", error);
      toast.error("Failed to download emails");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleDownloadEmails}
      isLoading={isDownloading}
    >
      <Download />
    </Button>
  );
};
